<template>
  <div>
    <WdsAlert v-if="projectEnded && !loading" warning icon>
      {{ $t('This project has been ended.') }}
      <template v-if="user.hasPermission('create-projects')" #footer>
        <WdsStack justify-start gap-sm class="tw-w-full tw-px-3">
          <WdsButton warning-subtle sm @click="showReopenProject = true">
            {{ $t('Reopen Project') }}
          </WdsButton>
          <WdsButton primary sm @click="showAddProjectModal = true">
            {{ $t('Create New Project') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsAlert>
    <div>
      <h4 class="m-b-20">{{ $t('Project') }} <WdsSpinner v-if="loading" indeterminate style="display: inline;"/>  </h4>
      <WdsStack justify-space-between>
        <h1 :class="{ 'animate-title': loading }">{{ project.name }}</h1>
        <ProjectActions v-if="!projectEnded" :project="project" @submitted="refresh" />
      </WdsStack>
      <div class="tw-text-gray-800 tw-text-lg tw-max-w-[800px]" :class="{ 'animate-description': loading }">
        {{ project.description }}
      </div>
      <div v-if="project?.owners?.length > 0" class="tw-text-gray-800 tw-text-lg">
        {{ $t('Project owners:') }}
        <div
          v-for="(owner, index) of project.owners"
          :key="owner.id"
          class="tw-inline-block tw-ml-1"
        >
          <ProfileInfoCard :global-id="owner.id">
            <a class="pointer">
              {{ owner.name }}
            </a>
            <span v-if="index + 1 < project.owners.length">&nbsp;|</span>
          </ProfileInfoCard>
        </div>
      </div>
      <div class="tw-max-w-[500px]">
        <ProgressBar
          class="tw-my-3"
          :label="$t('Budget')"
          :spent="project.spent"
          :budget="project.internalBudget"
          :allocated="project.combinedBudget"
          :currency="project.currency"
          :hide-progress="loading"
        />
      </div>
      <div v-if="!loading" class="add-jobs tw-flex tw-justify-end">
        <WdsButton
          v-if="user.hasPermission('manage-projects')"
          primary
          @click="showAddJobsModal = true"
        >
          {{ $t('Add jobs') }}
        </WdsButton>
        <WdsModal
          :show="showAddJobsModal"
          title="Add a job to your project"
          @cancel="showAddJobsModal = false"
        >
          <ModalAddJobs
            :project="project"
            @cancel="showAddJobsModal = false"
            @add-jobs="addedJobs"
          ></ModalAddJobs>
        </WdsModal>
      </div>

      <div class="card card-white">
        <BaseDataTable
          :paginator-info="project?.jobs?.paginatorInfo"
          :columns="columns"
          :entries="jobs"
          :loading="loading"
          @search="search"
        >
          <template #no-results>
            <div>
              {{ loading ? $t('Loading') : $t('No results') }}
            </div>
          </template>
          <template #hires="{ row }">
            <WdsText>{{ row.hires.data.length }}</WdsText>
          </template>
          <template #owners="{ row }">
            <template v-if="row.owners?.length">
              <GhostComponent
                v-for="(owner, index) in row.owners"
                :key="owner.id"
                :global-id="owner.id"
                component-name-when-active="ProfileInfoCard"
              >
                <a>
                  {{ owner.name }}
                  <template v-if="index < row.owners.length - 1"><br /></template>
                </a>
              </GhostComponent>
            </template>
            <WdsText v-else>{{ user.name }}</WdsText>
          </template>
          <template v-if="user.hasPermission('manage-projects')" #actions="{ row }">
            <WdsButtonGhost class="direct-hire-button tw-shrink-0" @click="removeJob(row)">
              {{ $t('Remove job') }}
            </WdsButtonGhost>
          </template>
        </BaseDataTable>
      </div>
    </div>

    <ModalEditProjectStatus
      v-if="showReopenProject"
      status="open"
      :project="project"
      @close="showReopenProject = false"
      @submitted="refresh"
    />
    <ModalRemoveJobFromProject
      :show="showRemoveJobFromProject"
      :project="project"
      :job="job"
      @close="showRemoveJobFromProject = false"
      @removed="refresh"
    />
    <ModalNewProject
      :show="showAddProjectModal"
      :account-id="accountId"
      @cancel="showAddProjectModal = false"
      @cancel-create="showAddProjectModal = false"
    />
  </div>
</template>

<script>
import { markRaw } from 'vue'
import gql from 'graphql-tag'
import {
  WdsStack,
  WdsSpinner,
  WdsModal,
  WdsText,
  WdsButton,
  WdsAlert,
  WdsButtonGhost,
} from '@worksome/wds-components'
import ModalAddJobs from '@/components/Projects/ModalAddJobs.vue'
import BaseDataTable from '@/components/BaseDataTable'
import ProgressBar from '@/components/Projects/ProgressBar.vue'
import ProjectActions from '@/components/Projects/ProjectActions.vue'
import GhostComponent from '@/components/GhostComponent.vue'
import { TdJob } from '@/components/BaseDataTable/Cells'
import { toFormattedAmountByLocale } from '@/utils/currency'
import ModalEditProjectStatus from '@/components/Projects/ModalEditProjectStatus.vue'
import ModalRemoveJobFromProject from '@/components/Projects/ModalRemoveJobFromProject.vue'
import ModalNewProject from '@/components/Projects/ModalNewProject.vue'
import ProfileInfoCard from '@/components/ProfileInfoCard.vue'

export default {
  components: {
    ProfileInfoCard,
    ModalNewProject,
    ModalRemoveJobFromProject,
    ModalEditProjectStatus,
    ProjectActions,
    ProgressBar,
    BaseDataTable,
    WdsButton,
    WdsModal,
    WdsAlert,
    WdsText,
    WdsSpinner,
    WdsButtonGhost,
    WdsStack,
    ModalAddJobs,
    GhostComponent,
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showReopenProject: false,
      showAddProjectModal: false,
      showAddJobsModal: false,
      showRemoveJobFromProject: false,
      job: null,
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      searchValue: '',
      columns: [
        {
          key: 'name',
          title: $t('Name'),
          class: 'vmiddle',
          searchKeys: ['name'],
          renderUsing: {
            component: markRaw(TdJob),
            componentProps: (row) => ({
              globalId: row.id,
              name: row.name,
              isCompleted: row.completed,
              companyName: this.account?.isOrganization ? row.company?.name : '',
            }),
          },
        },
        {
          key: 'hires',
          title: $t('Hires'),
          slot: 'hires',
          class: 'vmiddle',
          orderAble: true,
          searchKeys: ['hire'],
        },
        {
          key: 'budget',
          title: $t('Budget'),
          renderUsing: {
            component: markRaw(ProgressBar),
            componentProps(project) {
              return {
                currency: project.currency,
                budget: project.internalBudget ?? 0,
                spent: project.spent,
              }
            },
          },
          class: 'vmiddle tw-w-64 tw-min-w-full',
          orderAble: false,
        },
        {
          key: 'owners',
          title: $t('Owner(s)'),
          slot: 'owners',
          class: 'vmiddle',
          searchKeys: ['owners'],
        },
        {
          key: 'actions',
          slot: 'actions',
          title: $t('Actions'),
          class: 'vmiddle text-center',
        },
      ],
    }
  },
  apollo: {
    $client: 'publicApi',
    projectQuery: {
      query: gql`
        query project($id: ID!, $page: Int!, $count: Int!, $search: String) {
          project(id: $id) {
            id
            name
            description
            internalBudget
            jobs(first: $count, page: $page, search: $search) {
              data {
                id
                name
                publishedAt
                currency
                internalBudget
                spent
                completed
                owners {
                  id
                  name
                  email
                }
                hires {
                  data {
                    id
                  }
                }
              }
              paginatorInfo {
                count
                currentPage
                total
                ...PaginatorInfoFragment
              }
            }
            creator {
              ... on User {
                id
                name
              }
            }
            endDate
            currency
            internalBudget
            allocatedBudget
            spent
            owners {
              name
              id
            }
          }

          accounts {
            id
            ... on Company {
              teamMembers {
                data {
                  id
                  name
                }
              }
            }
          }
        }

        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      variables() {
        return {
          id: this.projectId,
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
          search: this.searchValue,
        }
      },
      update(data) {
        return data.project
      },
    },
  },
  computed: {
    accountId() {
      return this?.account?.globalId
    },
    loading() {
      return this.$apollo.loading
    },
    project() {
      return {
        ...this.projectQuery,
        internalBudget: this.projectQuery?.internalBudget,
        combinedBudget: this.projectQuery?.allocatedBudget,
        spent: this.projectQuery?.spent,
        currency: this.projectQuery?.currency,
      }
    },
    jobs() {
      return this.projectQuery?.jobs.data.map((result) => ({
        ...result,
        budget: `${result.currency} ${toFormattedAmountByLocale(
          (result.internalBudget ?? 0).toFixed(2),
        )}`,
        spent: result.spent,
      }))
    },
    projectEnded() {
      return this.project.endDate !== null
    },
  },
  methods: {
    refresh() {
      this.$apollo.queries.projectQuery.refetch()
    },
    addedJobs() {
      this.showAddJobsModal = false
      this.refresh()
    },
    removeJob(job) {
      this.job = job
      this.showRemoveJobFromProject = true
    },
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.searchValue = options.search
    },
  },
}
</script>

<style scoped lang="scss">
@tailwind base;
:deep(.budget) {
  width: inherit;
}

.animate-title {
  @apply tw-animate-pulse;
  @apply tw-cursor-wait;
  min-height: 20px;
  min-width: 200px;
  border-radius: 50px;
  @apply tw-bg-gray-100;
}

.animate-description {
  @apply tw-animate-pulse;
  @apply tw-cursor-wait;
  min-height: 10px;
  min-width: 350px;
  border-radius: 50px;
  @apply tw-bg-gray-100;
}
</style>
